import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const IDLE_LIMIT_MS = 15 * 60 * 1000; // auto-logout after 15 min of inactivity

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const logoutRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data.access_token) localStorage.setItem("token", data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("token");
    setUser(false);
  };
  logoutRef.current = logout;

  const has = (code) =>
    !!user && (user.is_admin || (Array.isArray(code) ? code.some((c) => (user.permissions || []).includes(c)) : (user.permissions || []).includes(code)));

  // Inactivity auto-logout (cross-tab via localStorage 'lastActivity').
  useEffect(() => {
    if (!user) return;
    let last = Date.now();
    localStorage.setItem("lastActivity", String(last));
    const bump = () => {
      const n = Date.now();
      if (n - last > 5000) {
        last = n;
        localStorage.setItem("lastActivity", String(n));
      }
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click", "mousemove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      const stored = parseInt(localStorage.getItem("lastActivity") || "0", 10);
      if (Date.now() - stored > IDLE_LIMIT_MS) {
        clearInterval(iv);
        logoutRef.current && logoutRef.current();
      }
    }, 30 * 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(iv);
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, setUser, login, logout, has }}>
      {children}
    </AuthCtx.Provider>
  );
}
